import { useEffect, useState, useMemo, useCallback } from 'react';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import './ShopAsesoriasPage.css';
import { logError } from '../../utils/logger';
import { formatARS } from '../../utils/currency';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

// Función auxiliar para normalizar fechas (solo fecha, sin hora)
const normalizeDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  // Crear una nueva fecha con solo año, mes y día (en hora local)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// Componente de Calendario Semanal
const WeeklyCalendar = ({ asesorias, selectedDate, onDateSelect }) => {
  const [currentWeek, setCurrentWeek] = useState(new Date());

  // Obtener el inicio de la semana (lunes)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajustar para que lunes sea el primer día
    return new Date(d.setDate(diff));
  };

  // Obtener los días de la semana
  const getWeekDays = () => {
    const start = getWeekStart(currentWeek);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    return days;
  };

  // Obtener asesorías disponibles para un día específico
  const getAsesoriasForDay = (date) => {
    if (!asesorias || !Array.isArray(asesorias)) return [];
    const normalizedDate = normalizeDate(date);
    return asesorias.filter(asesoria => {
      if (!asesoria.start_time || !asesoria.disponible) return false;
      const normalizedAsesoriaDate = normalizeDate(asesoria.start_time);
      if (!normalizedAsesoriaDate || !normalizedDate) return false;
      // Comparar las fechas normalizadas
      return normalizedAsesoriaDate.getTime() === normalizedDate.getTime();
    });
  };

  // Navegar a la semana anterior
  const goToPreviousWeek = () => {
    setCurrentWeek(new Date(currentWeek.getTime() - 7 * 24 * 60 * 60 * 1000));
  };

  // Navegar a la semana siguiente
  const goToNextWeek = () => {
    setCurrentWeek(new Date(currentWeek.getTime() + 7 * 24 * 60 * 60 * 1000));
  };

  // Ir a la semana actual
  const goToCurrentWeek = () => {
    setCurrentWeek(new Date());
  };

  const weekDays = getWeekDays();
  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

  const currentMonth = monthNames[currentWeek.getMonth()];
  const currentYear = currentWeek.getFullYear();

  return (
    <div className="asesorias-calendar">
      <div className="asesorias-calendar-header">
        <button className="asesorias-calendar-nav-btn" onClick={goToPreviousWeek}>
          <i className="bi-chevron-left"></i>
        </button>
        <div className="asesorias-calendar-title">
          <h3>{currentMonth} {currentYear}</h3>
          <button className="asesorias-calendar-today-btn" onClick={goToCurrentWeek}>
            Hoy
          </button>
        </div>
        <button className="asesorias-calendar-nav-btn" onClick={goToNextWeek}>
          <i className="bi-chevron-right"></i>
        </button>
      </div>
      <div className="asesorias-calendar-grid">
        {weekDays.map((day, index) => {
          const dayAsesorias = getAsesoriasForDay(day);
          const isToday = day.toDateString() === new Date().toDateString();
          const isSelected = selectedDate && day.toDateString() === selectedDate.toDateString();
          const isPast = day < new Date() && !isToday;

          return (
            <div
              key={index}
              className={`asesorias-calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isPast ? 'past' : ''}`}
              onClick={() => !isPast && onDateSelect(day)}
            >
              <div className="asesorias-calendar-day-name">{dayNames[index]}</div>
              <div className="asesorias-calendar-day-number">{day.getDate()}</div>
              {dayAsesorias.length > 0 && (
                <div className="asesorias-calendar-day-badge">
                  {dayAsesorias.length}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Componente de Card de Asesoría
const AsesoriaCard = ({ asesoria, onAddToCart, addingToCart }) => {
  const profesor = asesoria.profesor || {};
  const profesorNombre = `${profesor.Nombre || ''} ${profesor.apellido || ''}`.trim() || 'Profesor';
  const profesorFoto = resolveImageUrl(profesor.Foto);
  const especialidad = profesor.Especialidad || 'Sin especialidad';

  // Formatear fecha y hora
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Fecha por definir';
    const date = new Date(dateString);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('es-ES', options);
  };

  const startTime = asesoria.start_time ? formatDateTime(asesoria.start_time) : 'Fecha por definir';
  const endTime = asesoria.end_time ? formatDateTime(asesoria.end_time) : null;
  const precio = parseFloat(asesoria.Precio || 0);
  const isAdding = addingToCart === asesoria.id;

  return (
    <div className="asesoria-card">
      <div className="asesoria-card-header">
        {profesorFoto ? (
          <img src={profesorFoto} alt={profesorNombre} className="asesoria-card-profesor-img" />
        ) : (
          <div className="asesoria-card-profesor-placeholder">
            <i className="bi-person-circle"></i>
          </div>
        )}
        <div className="asesoria-card-profesor-info">
          <h4 className="asesoria-card-profesor-name">{profesorNombre}</h4>
          <p className="asesoria-card-profesor-especialidad">{especialidad}</p>
        </div>
      </div>
      <div className="asesoria-card-body">
        <div className="asesoria-card-time">
          <i className="bi-calendar-event"></i>
          <div>
            <strong>Inicio:</strong> {startTime}
            {endTime && (
              <>
                <br />
                <strong>Fin:</strong> {endTime}
              </>
            )}
          </div>
        </div>
        {asesoria.text && (
          <div className="asesoria-card-description">
            <p>{asesoria.text}</p>
          </div>
        )}
        {asesoria.Zoom && (
          <div className="asesoria-card-zoom">
            <i className="bi-camera-video"></i>
            <span>Vía Zoom</span>
          </div>
        )}
      </div>
      <div className="asesoria-card-footer">
        <div className="asesoria-card-price">
          <span className="asesoria-card-price-label">Precio:</span>
          <span className="asesoria-card-price-value">{formatARS(precio)}</span>
        </div>
        <button
          className="asesoria-card-add-btn"
          onClick={() => onAddToCart(asesoria)}
          disabled={isAdding}
        >
          {isAdding ? (
            <>
              <i className="bi-arrow-repeat spin"></i>
              Agregando...
            </>
          ) : (
            <>
              <i className="bi-cart-plus"></i>
              Agregar al Carrito
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const ShopAsesoriasPage = () => {
  const { token } = useAuth();
  const { addToCart } = useCart();
  const { notify } = useNotifications();
  const [asesorias, setAsesorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedProfesor, setSelectedProfesor] = useState('');
  const [addingToCart, setAddingToCart] = useState(null);

  // Obtener asesorías disponibles
  useEffect(() => {
    fetchAsesorias();
    // Refrescar cada 5 minutos para actualizar disponibilidad
    const interval = setInterval(fetchAsesorias, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchAsesorias = async () => {
    setLoading(true);
    try {
      // Usar ruta pública para obtener asesorías disponibles (sin autenticación)
      // Esto permite que cualquier usuario vea las asesorías disponibles en la tienda
      const response = await fetch(buildApiUrl('/api/asesorias/public'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Error al cargar asesorías');
      const data = await response.json();
      
      // Filtrar asesorías disponibles (no compradas, no expiradas)
      const now = new Date();
      const availableAsesorias = (data.asesorias || []).filter(asesoria => {
        // No debe tener usuario asignado (no comprada)
        if (asesoria.usuario_id) return false;
        
        // Debe estar marcada como disponible
        if (!asesoria.disponible) return false;
        
        // Si tiene end_time, no debe haber pasado
        if (asesoria.end_time) {
          const endDate = new Date(asesoria.end_time);
          if (endDate < now) return false;
        }
        
        return true;
      });

      setAsesorias(availableAsesorias);
    } catch (err) {
      logError('[ShopAsesoriasPage] Error fetching asesorías:', err);
      notify({ message: 'Error al cargar asesorías', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Obtener profesores únicos para el filtro
  const profesores = useMemo(() => {
    const uniqueProfesores = new Map();
    asesorias.forEach(asesoria => {
      const profesor = asesoria.profesor;
      if (profesor && profesor.id) {
        const key = profesor.id;
        if (!uniqueProfesores.has(key)) {
          uniqueProfesores.set(key, {
            id: profesor.id,
            nombre: `${profesor.Nombre || ''} ${profesor.apellido || ''}`.trim()
          });
        }
      }
    });
    return Array.from(uniqueProfesores.values());
  }, [asesorias]);

  // Filtrar asesorías según búsqueda, fecha y profesor
  const filteredAsesorias = useMemo(() => {
    let filtered = asesorias;

    // Filtro por búsqueda
    if (search.trim()) {
      const term = search.toLowerCase();
      filtered = filtered.filter(asesoria => {
        const profesor = asesoria.profesor || {};
        const profesorNombre = `${profesor.Nombre || ''} ${profesor.apellido || ''}`.toLowerCase();
        const especialidad = (profesor.Especialidad || '').toLowerCase();
        const text = (asesoria.text || '').toLowerCase();
        return profesorNombre.includes(term) || especialidad.includes(term) || text.includes(term);
      });
    }

    // Filtro por profesor
    if (selectedProfesor) {
      filtered = filtered.filter(asesoria => {
        return asesoria.profesor?.id === parseInt(selectedProfesor);
      });
    }

    // Filtro por fecha seleccionada
    if (selectedDate) {
      const normalizedSelectedDate = normalizeDate(selectedDate);
      filtered = filtered.filter(asesoria => {
        if (!asesoria.start_time) return false;
        const normalizedAsesoriaDate = normalizeDate(asesoria.start_time);
        if (!normalizedAsesoriaDate || !normalizedSelectedDate) return false;
        // Comparar las fechas normalizadas
        return normalizedAsesoriaDate.getTime() === normalizedSelectedDate.getTime();
      });
    }

    // Ordenar por fecha de inicio
    filtered.sort((a, b) => {
      const dateA = a.start_time ? new Date(a.start_time) : new Date(0);
      const dateB = b.start_time ? new Date(b.start_time) : new Date(0);
      return dateA - dateB;
    });

    return filtered;
  }, [asesorias, search, selectedProfesor, selectedDate]);

  // Agregar asesoría al carrito
  const handleAddToCart = useCallback(async (asesoria) => {
    setAddingToCart(asesoria.id);
    try {
      const profesor = asesoria.profesor || {};
      const profesorNombre = `${profesor.Nombre || ''} ${profesor.apellido || ''}`.trim() || 'Profesor';
      
      const cartItem = {
        id: asesoria.id,
        name: `Asesoría con ${profesorNombre}`,
        price: parseFloat(asesoria.Precio || 0),
        image: resolveImageUrl(profesor.Foto) || '',
        productType: 'asesoria',
        type: 'asesoria',
        referenceId: asesoria.id,
        quantity: 1,
        asesoriaData: {
          id: asesoria.id,
          profesor_id: asesoria.profesor_id,
          start_time: asesoria.start_time,
          end_time: asesoria.end_time,
          text: asesoria.text,
          Zoom: asesoria.Zoom
        }
      };

      const success = await addToCart(cartItem, token);
      if (success) {
        notify({ 
          message: 'Asesoría agregada al carrito exitosamente', 
          type: 'success' 
        });
      }
    } catch (err) {
      logError('[ShopAsesoriasPage] Error adding to cart:', err);
      notify({ message: 'Error al agregar al carrito', type: 'error' });
    } finally {
      setAddingToCart(null);
    }
  }, [addToCart, token, notify]);

  return (
    <div className="shop-asesorias-page">
      {/* Hero Section */}
      <div className="asesorias-hero">
        <div className="asesorias-hero-content">
          <div className="asesorias-hero-text">
            <span className="asesorias-hero-badge">Asesorías Personalizadas</span>
            <h1 className="asesorias-hero-title">
              Reserva tu <span className="asesorias-hero-title-accent">Asesoría</span>
            </h1>
            <p className="asesorias-hero-description">
              Conecta con nuestros <strong>profesores especializados</strong> en sesiones 
              personalizadas. Agenda tu consulta y recibe orientación experta adaptada a tus necesidades.
            </p>
          </div>
          <div className="asesorias-hero-image">
            <div className="asesorias-hero-placeholder">
              <i className="bi-calendar-check"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Section */}
      <div className="asesorias-calendar-section">
        <div className="asesorias-calendar-container">
          <h2 className="asesorias-section-title">Calendario Semanal</h2>
          <WeeklyCalendar
            asesorias={asesorias}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
          />
          {selectedDate && (
            <button
              className="asesorias-clear-filter-btn"
              onClick={() => setSelectedDate(null)}
            >
              <i className="bi-x-circle"></i>
              Limpiar filtro de fecha
            </button>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className="asesorias-filters-section">
        <div className="asesorias-filters-container">
          <div className="asesorias-search-box">
            <i className="bi-search"></i>
            <input
              type="text"
              placeholder="Buscar por profesor, especialidad o descripción..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="asesorias-search-input"
            />
          </div>
          {profesores.length > 0 && (
            <div className="asesorias-profesor-filter">
              <select
                value={selectedProfesor}
                onChange={(e) => setSelectedProfesor(e.target.value)}
                className="asesorias-profesor-select"
              >
                <option value="">Todos los profesores</option>
                {profesores.map(profesor => (
                  <option key={profesor.id} value={profesor.id}>
                    {profesor.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Asesorías Grid */}
      <div className="asesorias-grid-section">
        <div className="asesorias-grid-container">
          <h2 className="asesorias-section-title">
            {selectedDate 
              ? `Asesorías disponibles para ${selectedDate.toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
              : 'Asesorías Disponibles'
            }
          </h2>
          {loading ? (
            <div className="asesorias-loading">
              <div className="asesorias-spinner"></div>
              <p>Cargando asesorías...</p>
            </div>
          ) : filteredAsesorias.length === 0 ? (
            <div className="asesorias-empty-state">
              <i className="bi-calendar-x"></i>
              <h3>No se encontraron asesorías</h3>
              <p>
                {selectedDate || selectedProfesor || search
                  ? 'Intenta ajustar tus filtros de búsqueda'
                  : 'No hay asesorías disponibles en este momento. Vuelve pronto para ver nuevas opciones.'
                }
              </p>
            </div>
          ) : (
            <div className="asesorias-grid">
              {filteredAsesorias.map((asesoria) => (
                <AsesoriaCard
                  key={asesoria.id}
                  asesoria={asesoria}
                  onAddToCart={handleAddToCart}
                  addingToCart={addingToCart}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopAsesoriasPage;


